import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import * as pdfjs from "pdfjs-dist";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import PdfViewerModal from "./PdfViewerModal";
import "./PdfViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PdfPreviewCard({ pdfUrl, title }) {
  const previewRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(280);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasError, setHasError] = useState(!pdfUrl);
  const pdfFile = useMemo(() => (pdfUrl ? { url: pdfUrl } : null), [pdfUrl]);

  useEffect(() => {
    const node = previewRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setPreviewWidth(Math.max(220, Math.floor(node.clientWidth)));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setHasError(!pdfUrl);
  }, [pdfUrl]);

  const openModal = useCallback(() => {
    if (!hasError && pdfUrl) setIsModalOpen(true);
  }, [hasError, pdfUrl]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openModal();
    }
  };

  return (
    <>
      <article className="pdf-preview-card">
        <div
          className={`pdf-preview-shell ${hasError ? "is-unavailable" : ""}`}
          ref={previewRef}
          role="button"
          tabIndex={hasError ? -1 : 0}
          aria-label={`Open ${title} PDF preview`}
          onClick={openModal}
          onKeyDown={handleKeyDown}
        >
          {hasError ? (
            <PreviewUnavailable />
          ) : (
            <Document
              file={pdfFile}
              loading={<PreviewLoading />}
              error={<PreviewUnavailable />}
              onLoadSuccess={() => setHasError(false)}
              onLoadError={(error) => {
                console.warn("PDF preview failed to load", error);
                setHasError(true);
              }}
            >
              <Page
                pageNumber={1}
                width={previewWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<PreviewLoading />}
                error={<PreviewUnavailable />}
              />
            </Document>
          )}
          {!hasError && <span className="pdf-preview-action">View PDF</span>}
        </div>
        <div className="pdf-preview-body">
          <h3 className="pdf-preview-title">{title}</h3>
        </div>
      </article>

      {isModalOpen && (
        <PdfViewerModal
          pdfUrl={pdfUrl}
          title={title}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

function PreviewLoading() {
  return (
    <div className="pdf-preview-state">
      <span className="pdf-spinner" />
      <span>Loading preview...</span>
    </div>
  );
}

function PreviewUnavailable() {
  return (
    <div className="pdf-preview-state pdf-preview-unavailable">
      <span className="pdf-file-mark">PDF</span>
      <span>Preview unavailable</span>
    </div>
  );
}
